import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { dashboardApi } from '@/services/dashboardApi';
import { useDashboard } from '@/context/DashboardContext';
import { resolveCopy } from '@/config/copyRegistry';

const CopyContext = createContext(null);

/**
 * Loads admin-managed copy overrides from the backend on dashboard mount and
 * exposes them via context. The useCopy() hook reads from this context to
 * resolve strings with the override → registry default → key fallback chain.
 *
 * Mounted INSIDE DashboardProvider — only authenticated dashboard users fetch.
 * If the fetch fails (network, server down) we silently continue with empty
 * overrides; the registry defaults still render correctly.
 */
export function CopyProvider({ children }) {
  const { user } = useDashboard();
  const [overrides, setOverrides] = useState({});
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const data = await dashboardApi.listCopy();
      setOverrides(data || {});
    } catch (err) {
      console.error('Failed to load copy overrides:', err);
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    if (user) refresh();
    else {
      setOverrides({});
      setLoaded(false);
    }
  }, [user, refresh]);

  const updateLocal = useCallback((key, entry) => {
    setOverrides(prev => ({ ...prev, [key]: entry }));
  }, []);

  const removeLocal = useCallback((key) => {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  return (
    <CopyContext.Provider value={{ overrides, loaded, refresh, updateLocal, removeLocal }}>
      {children}
    </CopyContext.Provider>
  );
}

export function useCopyContext() {
  const ctx = useContext(CopyContext);
  if (!ctx) throw new Error('useCopyContext must be inside CopyProvider');
  return ctx;
}

/**
 * Primary hook for editable text in the dashboard.
 *
 *   const label = useCopy('home.subtitle');
 *
 * Resolution order:
 *   1. Admin-edited override from DB
 *   2. Default value from copyRegistry.js
 *   3. The key itself (only happens if you typo'd the key)
 */
export function useCopy(key, lang = 'es') {
  const { overrides } = useCopyContext();
  return resolveCopy(key, overrides, lang);
}

/**
 * Returns a stable resolver function — for places where keys are dynamic
 * (e.g. iterating over a list of nav items). Hooks can't be called inside
 * .map(), so use this once at the top of the component.
 *
 *   const t = useCopyResolver();
 *   items.map(i => <span>{t(i.labelKey)}</span>)
 */
export function useCopyResolver(lang = 'es') {
  const { overrides } = useCopyContext();
  return (key) => resolveCopy(key, overrides, lang);
}
